import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { BarChart, FunnelChart, LineChart, PieChart } from 'echarts/charts';
import { GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import ReactEchart from '../../../../components/common/ReactEchart';
import { useAppContext } from '../../../../providers/AppProvider';
import formatCurrency from '../../../../utils/formatCurrency';
import StatisticsCardLoading from '../../common/statistic-chart-card/StatisticsChartCardLoading';
import { STATISTICS_EMPTY_OVERDUE } from '../../common/lib/statisticsEmptyStateCopy';
import StatisticsCount from '../../common/StatisticsCount';
import StatisticsDonutLabelModeSwitch from '../../common/StatisticsDonutLabelModeSwitch';
import useStatisticsChartTheme from '../../common/hooks/useStatisticsChartTheme';
import { getStatisticsAccentColor } from '../../common/lib/statisticsChartPalette';
import StatisticsChartFrame from '../../common/StatisticsChartFrame';
import {
	DONUT_PIE_LABEL_MODE,
	getPrimaryPhaseChartTheme,
	mapDonutPieDataWithLabel,
	resolveDonutPieRadius,
	resolveFullPieRadius,
} from '../pipeline/lib/orderPrimaryPhaseChartTheme';
import { buildOverdueChartRows, buildOverdueSummaryStats, findOverdueRankItemByKey, resolveOverdueRankHighlightKey } from './lib/overdueStatChartData';
import {
	parseOverdueBucketIndex,
	resolveBucketChartColor,
	resolveDefaultAccentColor,
	resolveDefaultPaymentTypeColor,
	resolveOverdueAccentColor,
	resolveOverdueOtherColor,
	resolveOverduePaymentTypeColor,
	resolveOverdueRankColor,
} from './lib/overdueChartColors';
import {
	COMPOSITION_CHART_TYPE,
	isOverdueChartBarType,
	isOverdueChartFunnelType,
	isOverdueChartLineType,
	isOverdueChartPieLikeType,
	normalizeOverdueChartConfig,
	OVERDUE_CHART_DIMENSION,
	OVERDUE_CHART_RANGE_MODE,
} from './lib/overdueChartConfig';
import { isHorizontalBarOrientation, resolveBarBorderRadius, resolveBarLabelPosition } from '../shared/lib/barOrientation';
import OverdueChartSummaryStats from './OverdueChartSummaryStats';

echarts.use([TooltipComponent, LegendComponent, GridComponent, PieChart, BarChart, LineChart, FunnelChart, CanvasRenderer]);

const FILL_STYLE = { height: '100%', width: '100%', minHeight: 0 };

const formatMetricValue = (value, currencyMetric) => (currencyMetric ? formatCurrency(value) : `${value} 单`);

const formatOverdueRowDetail = (row, currencyMetric) => {
	const primary = formatMetricValue(row?.value ?? 0, currencyMetric);
	const lines = [primary];
	if (row?.arAmount != null && row?.orderCount != null) {
		lines.push(currencyMetric ? `${row.orderCount} 单` : formatCurrency(row.arAmount));
	}
	if (row?.memberLabels?.length) {
		const preview = row.memberLabels.slice(0, 8);
		const rest = row.memberLabels.length - preview.length;
		lines.push(`含：${preview.join('、')}${rest > 0 ? ` 等 ${row.memberLabels.length} 家` : ''}`);
	}
	return lines.join('<br/>');
};

const formatAxisAmount = (value) => {
	const n = Number(value);
	if (!Number.isFinite(n)) return '';
	if (Math.abs(n) >= 10000) return `${(n / 10000).toFixed(Math.abs(n) >= 100000 ? 0 : 1)}万`;
	if (Math.abs(n) >= 1000) return `${Math.round(n / 1000)}千`;
	return String(n);
};

const OverdueChart = ({
	overdueData = {},
	overdueConfig,
	isLoading = false,
	height = '11rem',
	compact = false,
	hideCategoryLabels = false,
	fillHeight = false,
	previewHeight = null,
	animationKey = 'overdue',
	selectedKey = null,
	onSelectRow,
	enableOrderDrill = false,
	onDrillSelect,
}) => {
	const { getThemeColor } = useAppContext();
	const { isLight } = useStatisticsChartTheme();
	const chartTheme = useMemo(() => getPrimaryPhaseChartTheme(isLight), [isLight]);
	const config = useMemo(() => normalizeOverdueChartConfig(overdueConfig), [overdueConfig]);
	const useFrameFill = Boolean(previewHeight || fillHeight);
	const slotHeight = previewHeight || height;
	const frameStyle = useFrameFill ? FILL_STYLE : { minHeight: height };
	const echartStyle = useFrameFill ? FILL_STYLE : { minHeight: height };
	const previewClass = useFrameFill ? 'statistics-overdue-chart--preview' : '';
	const isOverdueOnlyRange = config.rangeMode === OVERDUE_CHART_RANGE_MODE.OVERDUE_ONLY;
	const overdueToneClass = isOverdueOnlyRange ? 'statistics-overdue-chart--overdue-only' : '';

	const chartData = useMemo(() => buildOverdueChartRows(overdueData, config), [config, overdueData]);
	const { items, grouped, totalValue, currencyMetric } = chartData;
	const isDonut = config.chartType === COMPOSITION_CHART_TYPE.DONUT;
	const isPieLike = isOverdueChartPieLikeType(config.chartType);
	const isFunnel = isOverdueChartFunnelType(config.chartType);
	const isLine = isOverdueChartLineType(config.chartType);
	const isBar = isOverdueChartBarType(config.chartType);
	const horizontalBar = isBar && isHorizontalBarOrientation(config.barOrientation);
	const hideRankCategoryLabels = hideCategoryLabels && horizontalBar;
	const useGroupedSeries = (isBar || isLine) && config.dimension === OVERDUE_CHART_DIMENSION.BUCKET && grouped;
	const hasGroupedData = useGroupedSeries && grouped?.series?.some((series) => series.data.some((value) => value > 0));

	const overdueAccentColor = useMemo(() => resolveOverdueAccentColor(getThemeColor), [getThemeColor]);
	const defaultAccentColor = useMemo(() => resolveDefaultAccentColor(getThemeColor), [getThemeColor]);

	const resolveSliceColor = useCallback(
		(index, key) => {
			if (config.dimension === OVERDUE_CHART_DIMENSION.BUCKET) {
				const bucketIndex = parseOverdueBucketIndex(key);
				return resolveBucketChartColor(bucketIndex ?? index, getThemeColor);
			}
			if (config.dimension === OVERDUE_CHART_DIMENSION.PAYMENT_TYPE) {
				return isOverdueOnlyRange ? resolveOverduePaymentTypeColor(key, getThemeColor) : resolveDefaultPaymentTypeColor(key, getThemeColor);
			}
			if (config.dimension === OVERDUE_CHART_DIMENSION.CUSTOMER || config.dimension === OVERDUE_CHART_DIMENSION.SALE_USER) {
				if (key === '__other__') {
					return isOverdueOnlyRange ? resolveOverdueOtherColor(getThemeColor) : getThemeColor('secondary');
				}
				return isOverdueOnlyRange ? resolveOverdueRankColor(index, getThemeColor) : getStatisticsAccentColor(isLight, getThemeColor);
			}
			return resolveBucketChartColor(index, getThemeColor);
		},
		[config.dimension, getThemeColor, isLight, isOverdueOnlyRange],
	);

	const pieData = useMemo(
		() =>
			items.map((row, index) => ({
				name: row.label,
				value: row.value,
				row,
				itemStyle: { color: resolveSliceColor(index, row.key) },
			})),
		[items, resolveSliceColor],
	);
	const preserveEntitySliceOrder =
		(config.dimension === OVERDUE_CHART_DIMENSION.CUSTOMER || config.dimension === OVERDUE_CHART_DIMENSION.SALE_USER) && items.some((row) => row.key === '__other__');

	const [labelMode, setLabelMode] = useState(DONUT_PIE_LABEL_MODE.ALL);
	const [pickedLabelKeys, setPickedLabelKeys] = useState(() => new Set());
	const [localHighlightKey, setLocalHighlightKey] = useState(null);
	const rowSelectionControlled = typeof onSelectRow === 'function';
	const highlightKey = rowSelectionControlled ? resolveOverdueRankHighlightKey(items, selectedKey ?? null) : localHighlightKey;

	const selectedRow = useMemo(() => findOverdueRankItemByKey(items, selectedKey), [items, selectedKey]);
	const summaryStats = useMemo(
		() => buildOverdueSummaryStats(overdueData, { selectedRow, rangeMode: config.rangeMode }),
		[config.rangeMode, overdueData, selectedRow],
	);
	const selectedSliceKey = useMemo(() => selectedRow?.label ?? null, [selectedRow]);
	const centerDisplayValue = selectedRow?.value ?? totalValue;

	useEffect(() => {
		if (rowSelectionControlled) return;
		setLocalHighlightKey(null);
	}, [animationKey, config.dimension, config.chartType, config.metric, config.rangeMode, rowSelectionControlled]);

	const resolveRowFromClick = useCallback(
		(params) => {
			const directRow = params?.data?.row;
			if (directRow?.key) return directRow;
			const key = params?.data?.key;
			if (key) return items.find((row) => row.key === key) ?? null;
			if (config.dimension === OVERDUE_CHART_DIMENSION.BUCKET && params?.dataIndex != null) {
				return items.find((row) => row.key === `bucket-${params.dataIndex}`) ?? null;
			}
			const name = params?.data?.name ?? params?.name;
			if (name) return items.find((row) => row.label === name) ?? null;
			return null;
		},
		[config.dimension, items],
	);

	const toggleHighlightRow = useCallback(
		(row) => {
			if (!row?.key) return;
			const nextKey = highlightKey === row.key ? null : row.key;
			if (rowSelectionControlled) {
				onSelectRow(nextKey);
				return;
			}
			setLocalHighlightKey(nextKey);
		},
		[highlightKey, onSelectRow, rowSelectionControlled],
	);

	const emitOverdueDrill = useCallback(
		(row) => {
			if (!enableOrderDrill || !onDrillSelect || !row) return false;
			const orderIds = Array.isArray(row.orderIds) ? row.orderIds : [];
			if (!orderIds.length) return false;
			onDrillSelect({
				kind: 'overdue',
				orderIds,
				label: row.label,
			});
			return true;
		},
		[enableOrderDrill, onDrillSelect],
	);

	const handleSliceClick = useCallback(
		(params) => {
			const row = resolveRowFromClick(params);
			if (emitOverdueDrill(row)) {
				if (rowSelectionControlled) onSelectRow(row.key);
				else setLocalHighlightKey(row.key);
			} else if (row) {
				toggleHighlightRow(row);
			}
			const name = params?.data?.name;
			if (!name || labelMode !== DONUT_PIE_LABEL_MODE.PICK) return;
			setPickedLabelKeys((prev) => {
				const next = new Set(prev);
				if (next.has(name)) next.delete(name);
				else next.add(name);
				return next;
			});
		},
		[emitOverdueDrill, labelMode, onSelectRow, resolveRowFromClick, rowSelectionControlled, toggleHighlightRow],
	);

	const handleSeriesClick = useCallback(
		(params) => {
			const row = resolveRowFromClick(params);
			if (emitOverdueDrill(row)) {
				if (rowSelectionControlled) onSelectRow(row.key);
				else setLocalHighlightKey(row.key);
				return;
			}
			if (row) toggleHighlightRow(row);
		},
		[emitOverdueDrill, onSelectRow, resolveRowFromClick, rowSelectionControlled, toggleHighlightRow],
	);

	const handleLabelModeChange = useCallback((mode) => {
		setLabelMode(mode);
		if (mode === DONUT_PIE_LABEL_MODE.ALL) setPickedLabelKeys(new Set());
	}, []);

	const donutChartEvents = useMemo(() => ({ click: handleSliceClick }), [handleSliceClick]);
	const seriesChartEvents = useMemo(() => ({ click: handleSeriesClick }), [handleSeriesClick]);
	const pieHasLabelRoom = labelMode === DONUT_PIE_LABEL_MODE.ALL || pickedLabelKeys.size > 0;
	const pieCompact = parseFloat(slotHeight) <= 7;

	useEffect(() => {
		setPickedLabelKeys((prev) => {
			const valid = new Set(pieData.map((item) => item.name));
			const next = new Set([...prev].filter((key) => valid.has(key)));
			return next.size === prev.size ? prev : next;
		});
	}, [pieData]);

	const pieRadius = useMemo(() => {
		if (isDonut) return resolveDonutPieRadius(pieHasLabelRoom, pieCompact);
		return resolveFullPieRadius(pieHasLabelRoom, pieCompact);
	}, [isDonut, pieCompact, pieHasLabelRoom]);

	const pieOption = useMemo(
		() => ({
			backgroundColor: 'transparent',
			tooltip: {
				...chartTheme.tooltip,
				trigger: 'item',
				formatter: (params) => {
					const row = params.data?.row;
					return `${params.name}<br/>${formatOverdueRowDetail(row ?? { value: params.value }, currencyMetric)} (${params.percent}%)`;
				},
			},
			legend: {
				show: !isDonut && pieData.length > 1 && labelMode === DONUT_PIE_LABEL_MODE.PICK && pickedLabelKeys.size === 0,
				bottom: 0,
				left: 'center',
				itemWidth: 8,
				itemHeight: 8,
				textStyle: { color: chartTheme.labelColor, fontSize: 10 },
			},
			series: [
				{
					type: 'pie',
					radius: pieRadius,
					center: ['50%', !isDonut && pieData.length > 1 && labelMode === DONUT_PIE_LABEL_MODE.PICK && pickedLabelKeys.size === 0 ? '44%' : '50%'],
					padAngle: 2,
					sort: preserveEntitySliceOrder ? 'none' : 'desc',
					color: pieData.map((item) => item.itemStyle.color),
					selectedMode: false,
					avoidLabelOverlap: true,
					label: { show: false },
					labelLine: { show: false },
					cursor: 'pointer',
					data: mapDonutPieDataWithLabel(pieData, {
						chartTheme,
						getKey: (item) => item.name,
						labelMode,
						pickedKeys: pickedLabelKeys,
						selectedKey: selectedSliceKey,
						labelFormatter: (params) => `${params.name}\n${formatMetricValue(params.value, currencyMetric)} · ${params.percent}%`,
					}),
					animationDuration: 700,
				},
			],
		}),
		[chartTheme, currencyMetric, isDonut, labelMode, pickedLabelKeys, pieData, pieRadius, preserveEntitySliceOrder, selectedSliceKey],
	);

	const funnelOption = useMemo(
		() => ({
			backgroundColor: 'transparent',
			tooltip: {
				...chartTheme.tooltip,
				trigger: 'item',
				formatter: (params) => {
					const row = params.data?.row;
					return `${params.name}<br/>${formatOverdueRowDetail(row ?? { value: params.value }, currencyMetric)}`;
				},
			},
			legend: {
				show: pieData.length > 1,
				bottom: 0,
				left: 'center',
				itemWidth: 8,
				itemHeight: 8,
				textStyle: { color: chartTheme.labelColor, fontSize: 10 },
			},
			series: [
				{
					type: 'funnel',
					left: '8%',
					top: 8,
					bottom: pieData.length > 1 ? 28 : 8,
					width: '84%',
					minSize: '12%',
					maxSize: '100%',
					sort: preserveEntitySliceOrder ? 'none' : 'descending',
					gap: 4,
					label: {
						show: true,
						position: 'inside',
						fontSize: 9,
						color: '#fff',
						formatter: (params) => `${params.name}\n${formatMetricValue(params.value, currencyMetric)}`,
					},
					itemStyle: {
						borderColor: chartTheme.pieBorderColor,
						borderWidth: 1,
					},
					data: pieData.map((item) => ({
						name: item.name,
						value: item.value,
						key: item.row?.key,
						row: item.row,
						itemStyle: {
							...item.itemStyle,
							opacity: highlightKey && item.row?.key !== highlightKey ? 0.38 : 1,
						},
					})),
					animationDuration: 680,
				},
			],
		}),
		[chartTheme, currencyMetric, highlightKey, pieData, preserveEntitySliceOrder],
	);

	const groupedBarLineOption = useMemo(() => {
		if (!grouped) return null;
		const categoryLabels = grouped.labels;
		const valueAxis = {
			type: 'value',
			...(currencyMetric ? {} : { minInterval: 1 }),
			...chartTheme.axis,
			axisLabel: {
				...chartTheme.axis.axisLabel,
				formatter: currencyMetric ? formatAxisAmount : undefined,
			},
		};
		const categoryAxis = {
			type: 'category',
			data: categoryLabels,
			...(horizontalBar ? { inverse: true } : {}),
			...chartTheme.axis,
			splitLine: { show: false },
			axisLabel: {
				...chartTheme.axis.axisLabel,
				fontSize: 9,
				interval: 0,
				show: !hideRankCategoryLabels,
				rotate: !horizontalBar && categoryLabels.length > 4 ? 24 : 0,
			},
		};
		const deliveryColor = isOverdueOnlyRange ? resolveOverduePaymentTypeColor('delivery', getThemeColor) : resolveDefaultPaymentTypeColor('delivery', getThemeColor);
		const acceptColor = isOverdueOnlyRange ? resolveOverduePaymentTypeColor('accept', getThemeColor) : resolveDefaultPaymentTypeColor('accept', getThemeColor);

		const series = grouped.series.map((entry, seriesIndex) => {
			const seriesColor = seriesIndex === 0 ? deliveryColor : acceptColor;
			const pointData = entry.data.map((value, dataIndex) => {
				const bucketKey = `bucket-${dataIndex}`;
				const color = resolveBucketChartColor(dataIndex, getThemeColor);
				const row = items.find((item) => item.key === bucketKey);
				return {
					value,
					key: bucketKey,
					row,
					itemStyle: {
						...chartTheme.createBarStyle(color, highlightKey, bucketKey),
						...(isLine ? {} : { borderRadius: resolveBarBorderRadius(horizontalBar) }),
					},
				};
			});
			const base = {
				name: entry.label,
				type: isLine ? 'line' : 'bar',
				data: pointData,
				emphasis: chartTheme.barEmphasis,
			};
			if (isLine) {
				return {
					...base,
					smooth: 0.35,
					symbolSize: 5,
					lineStyle: { width: 2.2, color: seriesColor },
				};
			}
			return {
				...base,
				barMaxWidth: hideRankCategoryLabels ? 14 : 16,
				...(hideRankCategoryLabels ? { barCategoryGap: '10%' } : {}),
				cursor: 'pointer',
				label: {
					show: !compact,
					position: resolveBarLabelPosition(horizontalBar),
					fontSize: 9,
					color: chartTheme.labelColor,
					formatter: (params) => (params.value > 0 ? formatMetricValue(params.value, currencyMetric) : ''),
				},
			};
		});

		return {
			backgroundColor: 'transparent',
			grid: hideRankCategoryLabels
				? { left: 4, right: 10, top: grouped.series.length > 1 ? 24 : 4, bottom: 2, containLabel: false }
				: { left: 8, right: 16, top: grouped.series.length > 1 ? 28 : 10, bottom: horizontalBar ? 4 : 8, containLabel: true },
			legend: {
				show: grouped.series.length > 1,
				top: 0,
				right: 0,
				itemWidth: 8,
				itemHeight: 8,
				textStyle: { color: chartTheme.labelColor, fontSize: 10 },
			},
			tooltip: {
				...chartTheme.tooltip,
				trigger: 'axis',
				axisPointer: { type: isLine ? 'line' : 'shadow', shadowStyle: { color: chartTheme.axisPointerShadow } },
				formatter: (params) => {
					if (!params?.length) return '';
					const lines = [params[0].name];
					params.forEach((item) => {
						lines.push(`${item.seriesName}: ${formatMetricValue(item.value, currencyMetric)}`);
					});
					return lines.join('<br/>');
				},
			},
			xAxis: horizontalBar ? valueAxis : categoryAxis,
			yAxis: horizontalBar ? categoryAxis : valueAxis,
			series,
		};
	}, [chartTheme, compact, currencyMetric, getThemeColor, grouped, hideRankCategoryLabels, highlightKey, horizontalBar, isLine, isOverdueOnlyRange, items]);

	const singleBarLineOption = useMemo(() => {
		const categoryLabels = items.map((row) => row.label);
		const values = items.map((row, index) => {
			const color = resolveSliceColor(index, row.key);
			return {
				value: row.value,
				key: row.key,
				row,
				itemStyle: {
					...chartTheme.createBarStyle(color, highlightKey, row.key),
					borderRadius: resolveBarBorderRadius(horizontalBar),
				},
			};
		});
		const accentColor = isOverdueOnlyRange ? overdueAccentColor : defaultAccentColor;
		const usePerPointLineColor = config.dimension === OVERDUE_CHART_DIMENSION.BUCKET;
		const valueAxis = {
			type: 'value',
			...(currencyMetric ? {} : { minInterval: 1 }),
			...chartTheme.axis,
			axisLabel: {
				...chartTheme.axis.axisLabel,
				formatter: currencyMetric ? formatAxisAmount : undefined,
			},
		};
		const categoryAxis = {
			type: 'category',
			data: categoryLabels,
			...(horizontalBar ? { inverse: true } : {}),
			...chartTheme.axis,
			splitLine: { show: false },
			axisLabel: {
				...chartTheme.axis.axisLabel,
				fontSize: 9,
				interval: 0,
				show: !hideRankCategoryLabels,
				rotate: !horizontalBar && categoryLabels.length > 4 ? 24 : 0,
			},
		};
		return {
			backgroundColor: 'transparent',
			grid: hideRankCategoryLabels
				? { left: 4, right: 10, top: 4, bottom: 2, containLabel: false }
				: { left: 8, right: 16, top: 14, bottom: horizontalBar ? 4 : 8, containLabel: true },
			tooltip: {
				...chartTheme.tooltip,
				trigger: 'axis',
				axisPointer: { type: isLine ? 'line' : 'shadow', shadowStyle: { color: chartTheme.axisPointerShadow } },
				formatter: (params) => {
					const item = params?.[0];
					if (!item) return '';
					const row = items[item.dataIndex];
					return `${item.name}<br/>${formatOverdueRowDetail(row ?? { value: item.value }, currencyMetric)}`;
				},
			},
			xAxis: horizontalBar ? valueAxis : categoryAxis,
			yAxis: horizontalBar ? categoryAxis : valueAxis,
			series: [
				isLine
					? {
							type: 'line',
							smooth: 0.35,
							data: values,
							symbolSize: 6,
							lineStyle: { width: 2.5, color: usePerPointLineColor ? chartTheme.labelColor : accentColor },
							...(usePerPointLineColor ? {} : { itemStyle: { color: accentColor }, areaStyle: { color: accentColor, opacity: 0.08 } }),
							label: {
								show: !compact,
								position: 'top',
								fontSize: 9,
								color: chartTheme.labelColor,
								formatter: (params) => formatMetricValue(params.value, currencyMetric),
							},
							animationDuration: 680,
						}
					: {
							type: 'bar',
							data: values,
							barMaxWidth: hideRankCategoryLabels ? 14 : 18,
							...(hideRankCategoryLabels ? { barCategoryGap: '10%' } : {}),
							emphasis: chartTheme.barEmphasis,
							cursor: 'pointer',
							label: {
								show: !compact,
								position: resolveBarLabelPosition(horizontalBar),
								fontSize: 9,
								color: chartTheme.labelColor,
								formatter: (params) => formatMetricValue(params.value, currencyMetric),
							},
							animationDuration: 680,
						},
			],
		};
	}, [
		chartTheme,
		compact,
		config.dimension,
		currencyMetric,
		defaultAccentColor,
		hideRankCategoryLabels,
		highlightKey,
		horizontalBar,
		isLine,
		isOverdueOnlyRange,
		items,
		overdueAccentColor,
		resolveSliceColor,
	]);

	const renderChartBody = () => {
		if (isLoading) {
			return (
				<StatisticsCardLoading
					className={['statistics-overdue-chart statistics-overdue-chart--loading', previewClass, useFrameFill ? 'statistics-lite-chart__loading--fill' : null]
						.filter(Boolean)
						.join(' ')}
					style={frameStyle}
					fill={useFrameFill}
					label='加载超期统计…'
				/>
			);
		}

		if (!items.length && !hasGroupedData) {
			return (
				<div className={['statistics-overdue-chart statistics-overdue-chart--empty', previewClass].filter(Boolean).join(' ')} style={frameStyle}>
					<span className='text-muted small'>{STATISTICS_EMPTY_OVERDUE}</span>
				</div>
			);
		}

		if (isPieLike) {
			const hasLegend = !isDonut && pieData.length > 1 && labelMode === DONUT_PIE_LABEL_MODE.PICK && pickedLabelKeys.size === 0;
			return (
				<div
					className={[
						'statistics-lite-chart__pie-wrap',
						pieHasLabelRoom ? 'statistics-lite-chart__pie-wrap--selected' : null,
						'statistics-overdue-chart',
						overdueToneClass,
						isDonut ? 'statistics-overdue-chart--donut' : 'statistics-overdue-chart--pie',
						hasLegend ? 'statistics-overdue-chart--has-legend' : null,
						previewClass,
					]
						.filter(Boolean)
						.join(' ')}
					style={frameStyle}
				>
					<StatisticsDonutLabelModeSwitch mode={labelMode} onChange={handleLabelModeChange} />
					<StatisticsChartFrame className={pieHasLabelRoom ? 'statistics-chart-frame--donut-selected' : ''} style={frameStyle} fillSlot={useFrameFill}>
						<ReactEchart
							key={`overdue-pie-${config.chartType}-${config.dimension}-${config.metric}-${animationKey}`}
							echarts={echarts}
							option={pieOption}
							onEvents={donutChartEvents}
							style={echartStyle}
						/>
					</StatisticsChartFrame>
					{isDonut ? (
						<div className='statistics-lite-chart__pie-center'>
							<strong className='statistics-lite-chart__pie-center-link statistics-composition-chart__center'>
								{currencyMetric ? (
									<span className='statistics-composition-chart__center-currency'>{formatCurrency(centerDisplayValue)}</span>
								) : (
									<span className='statistics-composition-chart__center-count-row'>
										<StatisticsCount value={centerDisplayValue} loading={isLoading} duration={0.45} />
										<span className='statistics-composition-chart__center-label'>单</span>
									</span>
								)}
							</strong>
						</div>
					) : null}
				</div>
			);
		}

		if (isFunnel) {
			return (
				<div className={['statistics-overdue-chart statistics-overdue-chart--funnel', previewClass].filter(Boolean).join(' ')} style={frameStyle}>
					<StatisticsChartFrame style={frameStyle} fillSlot={useFrameFill}>
						<ReactEchart
							key={`overdue-funnel-${config.dimension}-${config.metric}-${animationKey}`}
							echarts={echarts}
							option={funnelOption}
							onEvents={seriesChartEvents}
							style={echartStyle}
						/>
					</StatisticsChartFrame>
				</div>
			);
		}

		const barLineOption = useGroupedSeries ? groupedBarLineOption : singleBarLineOption;
		const chartTypeClass = isLine ? 'statistics-overdue-chart--line' : 'statistics-overdue-chart--bar';

		return (
			<div className={['statistics-overdue-chart', overdueToneClass, chartTypeClass, previewClass].filter(Boolean).join(' ')} style={frameStyle}>
				<StatisticsChartFrame style={frameStyle} fillSlot={useFrameFill}>
					<ReactEchart
						key={`overdue-${config.chartType}-${config.dimension}-${config.metric}-${config.barOrientation}-${animationKey}`}
						echarts={echarts}
						option={barLineOption}
						onEvents={seriesChartEvents}
						style={echartStyle}
					/>
				</StatisticsChartFrame>
			</div>
		);
	};

	const hasChartData = items.length > 0 || hasGroupedData;
	const summaryNode = hasChartData ? (
		<OverdueChartSummaryStats
			salesTotal={summaryStats.salesTotal}
			overdueTotal={summaryStats.overdueTotal}
			orderCount={summaryStats.orderCount}
			loading={isLoading}
			isFiltered={Boolean(selectedRow)}
		/>
	) : null;

	if (useFrameFill) {
		const wrapperClassName = [
			'statistics-overdue-stat-chart',
			previewHeight ? 'statistics-overdue-stat-chart--preview' : null,
			fillHeight ? 'statistics-overdue-stat-chart--fill' : null,
		]
			.filter(Boolean)
			.join(' ');

		return (
			<div className={wrapperClassName} style={FILL_STYLE}>
				<div className='statistics-overdue-stat-chart__plot' style={FILL_STYLE}>
					{renderChartBody()}
				</div>
				{summaryNode}
			</div>
		);
	}

	return (
		<div className={['statistics-overdue-stat-chart', compact ? 'statistics-overdue-stat-chart--compact' : null].filter(Boolean).join(' ')}>
			<div className='statistics-overdue-stat-chart__plot' style={{ height, minHeight: height }}>
				{renderChartBody()}
			</div>
			{summaryNode}
		</div>
	);
};

OverdueChart.propTypes = {
	overdueData: PropTypes.object,
	overdueConfig: PropTypes.object,
	isLoading: PropTypes.bool,
	height: PropTypes.string,
	compact: PropTypes.bool,
	hideCategoryLabels: PropTypes.bool,
	fillHeight: PropTypes.bool,
	previewHeight: PropTypes.string,
	animationKey: PropTypes.string,
	selectedKey: PropTypes.string,
	onSelectRow: PropTypes.func,
	enableOrderDrill: PropTypes.bool,
	onDrillSelect: PropTypes.func,
};

export default memo(OverdueChart);
